/**
Copyright (c) 2023, 2024, 2025 Amica Corp.
All rights reserved.

This source code, resulting binary forms, and associated documentation
(the "Software") may only be used, modified, or distributed, with or
without modification, under the terms of a written license agreement
with Amica Corp. (the "License"). All rights to manufacture, use,
redistribute, or sell anything described herein, in whole or in part,
are granted only as explicitly permitted by such License. The receipt
or possession of the Software does not convey any of these rights.

This Software is provided "AS IS," WITHOUT WARRANTY OF ANY KIND, express
or implied, including but not limited to the implied warranties of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.

For additional terms, see https://amica.cloud/software-copyright/
 */

import {
  Button,
  TextField,
  Alert,
  FormControl,
  FormControlLabel,
  Switch,
  Grid,
  Box,
} from '@mui/material';
import { Formik } from 'formik';
import { useDispatch, useSelector } from 'react-redux';
import { selectNotification } from 'modules/common/notifications/selectors';
import { selectOrganizationId } from 'modules/common/auth/selectors';
import ERROR_TYPES from 'modules/common/constants/error-types';
import { useEffect, useState } from 'react';
import { isEmpty } from 'modules/common/helpers/object';
import { systemActions } from 'modules/system/slice';
import { selectSystemInformation } from 'modules/system/selectors';

const AddSystemNotice = () => {
  const FormStyles = { borderRadius: 10 };
  const dispatch = useDispatch();
  //
  const organizationId = useSelector(selectOrganizationId);
  const notification = useSelector(selectNotification);
  const systemInfo = useSelector(selectSystemInformation);
  //
  useEffect(() => {
    const query = `limit=5`;
    dispatch(
      systemActions.getSystemInformation({
        query,
      })
    );
  }, [organizationId]);

  const [initialValues, setInitialValues] = useState({
    blobUrl: '',
    enabled: false,
  });
  //
  useEffect(() => {
    if (!isEmpty(systemInfo)) {
      setInitialValues({
        blobUrl: systemInfo?.value?.blobUrl,
        enabled: systemInfo?.value?.isSystemBannerEnabled,
      });
    }
  }, [systemInfo]);
  //
  const onSubmitCreateSystemData = async (values) => {
    const valueObj = {
      key: 'maintenance',
      value: {
        blobUrl: values.blobUrl,
        isSystemBannerEnabled: values.enabled,
      },
    };
    if (isEmpty(systemInfo)) {
      dispatch(systemActions.createSystemInformation(valueObj));
    } else {
      dispatch(
        systemActions.updateSystemInformation({ systemInformationId: systemInfo?.id, ...valueObj })
      );
    }
  };
 //
  return (
    <Box ml={4} mt={2} component={Grid} container item display="flex" direction="column">
      <Formik initialValues={initialValues} onSubmit={onSubmitCreateSystemData} enableReinitialize>
        {({ handleBlur, handleChange, handleSubmit, isSubmitting, values, resetForm }) => (
          <form noValidate onSubmit={handleSubmit}>
            {notification?.isEnabled && notification?.type === ERROR_TYPES.ERROR && (
              <Alert mt={2} mb={3} severity={notification?.type}>
                {notification?.message}
              </Alert>
            )}
            <Grid justifyContent="space-around" alignItems="center" px={4} my={4}>
              <FormControl sx={FormStyles} fullWidth>
                <TextField
                  size="small"
                  name="blobUrl"
                  value={values?.blobUrl}
                  onBlur={handleBlur}
                  onChange={handleChange}
                  fullWidth
                  label="HTML Blob URL"
                  my={2}
                />
              </FormControl>
            </Grid>
            <Grid justifyContent="space-around" alignItems="center" px={4} my={4}>
              <FormControl sx={FormStyles} fullWidth>
                <FormControl sx={{ paddingTop: 3, paddingBottom: 3 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={values?.enabled}
                        name="enabled"
                        onBlur={handleBlur}
                        onChange={handleChange}
                      />
                    }
                    label="Enabled"
                    sx={{
                      width: 'fit-content',
                      height: 10,
                    }}
                  />
                </FormControl>
              </FormControl>
            </Grid>
            <Grid justifyContent="space-around" alignItems="center" px={4} my={4}>
              <Button
                onClick={() => {
                  resetForm();
                }}
                variant="contained"
                color="primary"
                sx={{ mr: 10 }}
              >
                Cancel
              </Button>
              <Button type="submit" variant="contained" color="success" disabled={isSubmitting}>
                Save
              </Button>
            </Grid>
          </form>
        )}
      </Formik>
    </Box>
  );
};
//
export default AddSystemNotice;
